import { type CSSProperties, useLayoutEffect, useRef, useState } from "react";

import VirtualList from "react-tiny-virtual-list";

import { styled } from "styled-components";

import { Sortable } from "components/lib/dnd/Sortable";

import CustomLaneCard from "../CustomLaneCard";
import { estimateLaneCardHeight } from "./estimateLaneCardHeight";
import type { IIdeaCard, ILaneData } from "./index";

type LaneMeasuredVirtualListProps = { lane: ILaneData; loading: boolean };

const WrapCard = styled.div`
  display: flex;
  flex: 1;
`;

type LaneVirtualListRowProps = {
  ideaId: string;
  index: number;
  card: IIdeaCard;
  virtualStyle: CSSProperties;
  loading: boolean;
  cachedHeight: number | undefined;
  onCommitHeight: (ideaId: string, height: number) => void;
};

const CARD_PADDING = 3;

function LaneVirtualListRow({
  ideaId,
  index,
  card,
  virtualStyle,
  loading,
  onCommitHeight,
}: LaneVirtualListRowProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const h = Math.ceil(el.scrollHeight) + CARD_PADDING * 2;
    onCommitHeight(ideaId, h);
  }, [ideaId, loading, onCommitHeight, card]);

  const rowStyle: CSSProperties = {
    ...virtualStyle,
    display: "flex",
    flex: 1,
    padding: `${CARD_PADDING}px 0px`,
  };

  return (
    <Sortable
      id={ideaId}
      index={index}
      accept={["card"]}
      type="card"
      disabled={card.draggingDisabled}
      style={{ ...rowStyle, display: "flex", alignItems: "flex-start" }}
    >
      <WrapCard ref={wrapRef}>
        <CustomLaneCard card={card} />
      </WrapCard>
    </Sortable>
  );
}

export function LaneMeasuredVirtualList({
  lane,
  loading,
}: LaneMeasuredVirtualListProps) {
  const [heightCache, setHeightCache] = useState<Record<string, number>>({});

  const onCommitHeight = (ideaId: string, h: number) => {
    setHeightCache((prev) => {
      if (prev[ideaId] === h) return prev;
      return { ...prev, [ideaId]: h };
    });
  };

  const itemSize = (index: number) => {
    const ideaId = lane.cards[index]?.idea._id;
    if (ideaId) {
      const cached = heightCache[ideaId];
      if (cached !== undefined) return cached;
    }
    if (loading) {
      return 100;
    }
    return estimateLaneCardHeight(lane.cards[index]);
  };

  const renderItem = ({
    index,
    style,
  }: {
    index: number;
    style: CSSProperties;
  }) => {
    const card = lane.cards[index];
    if (!card) return null;

    const ideaId = card.idea._id;
    return (
      <LaneVirtualListRow
        key={ideaId}
        ideaId={ideaId}
        index={index}
        card={card}
        cachedHeight={heightCache[ideaId]}
        virtualStyle={style}
        loading={loading}
        onCommitHeight={onCommitHeight}
      />
    );
  };

  const combinedCachedCardHeight = lane.cards.reduce(
    (acc, curr) =>
      acc + (heightCache[curr.idea._id] ?? estimateLaneCardHeight(curr)),
    0,
  );
  const listHeight = Math.min(
    window.innerHeight,
    combinedCachedCardHeight + CARD_PADDING,
  );

  return (
    <VirtualList
      style={{ scrollbarWidth: "thin", paddingBottom: CARD_PADDING }}
      height={listHeight}
      itemCount={lane.cards.length}
      estimatedItemSize={250}
      itemSize={itemSize}
      renderItem={renderItem}
    />
  );
}
